import type { SystemColumn } from "./system-columns";

/**
 * The structure contract: shared vocabulary, not one feature's private types.
 *
 * It lives in `shared/` because BOTH the dynamic renderer and the structure
 * feature read it. Keeping it inside `features/structure` made `dynamic/` import
 * from a feature, which the architecture rule caught — and rightly: the renderer
 * must depend on the shape of a definition, never on the feature that fetches it.
 */
export type FieldType =
	| "text"
	| "long_text"
	| "number"
	| "integer"
	| "currency"
	| "boolean"
	| "date"
	| "datetime"
	| "time"
	| "select"
	| "multi_select"
	| "email"
	| "phone"
	| "url"
	| "tax_id"
	| "national_id"
	| "json"
	| "file"
	| "signature"
	| "rating"
	| "ref_entity"
	| "ref_contact"
	| "ref_user";

/** Uma origem de `config.copyFrom`: o vínculo (`ref_entity`) e o campo do registro escolhido. */
export interface CopyFromSource {
	readonly link: string;
	readonly field: string;
}

export interface FieldDefinition {
	readonly id: string;
	readonly code: string;
	readonly label: string;
	readonly type: FieldType;
	readonly required: boolean;
	/**
	 * Livre por campo; as chaves que o front lê com forma conhecida vão abaixo.
	 * `copyFrom` (uma origem ou várias): ao escolher o vínculo `link` (um `ref_entity` da mesma entidade),
	 * este campo recebe `field` do registro escolhido — cópia, editável depois.
	 */
	readonly config: Record<string, unknown> & {
		readonly copyFrom?: CopyFromSource | readonly CopyFromSource[];
	};
	readonly helpText: string | null;
	readonly sortOrder: number;
	readonly showInList: boolean;
	readonly archived: boolean;
}

export interface EntityDefinition {
	readonly process?: {
		readonly code: string;
		readonly stages: readonly {
			code: string;
			name: string;
			color: string;
			isTerminal?: boolean;
			objective?: string | null;
			exitWhen?: string | null;
		}[];
	} | null;
	readonly id: string;
	readonly code: string;
	readonly name: string;
	/**
	 * Esta pessoa pode ALTERAR esta entidade — a decisão do servidor, desenhada.
	 *
	 * Opcional porque a lista é lida por rotas que não a calculam (o blueprint, o
	 * construtor), e ausente quer dizer "não perguntei", nunca "não pode": quem
	 * desenha um botão a partir dela trata a ausência como o estado de hoje —
	 * aberto — e é o SERVIDOR que recusa, com a frase que nomeia o motivo.
	 */
	readonly canWrite?: boolean;
	readonly description: string | null;
	readonly icon: string | null;
	readonly showInNav: boolean;
	/**
	 * Lista de referência (catálogo, modelo, resposta pronta) que o documento mandou para
	 * Configurações → Cadastros (`showInNav: "settings"`): fora do menu, ainda visível.
	 */
	readonly inSettings?: boolean;
	readonly navGroup: string | null;
	readonly navOrder: number;
	readonly status: "active" | "archived";
	/**
	 * What this entity is FOR, when a core screen needs to find it — `snippet` for
	 * canned replies.
	 *
	 * A screen binds to the ROLE and never to the code, so whichever entity plays the part
	 * is the one it uses — whatever it is called and whoever created it. Null for almost
	 * every entity; see migration 0042.
	 */
	readonly role?: string | null;
	/**
	 * Natural keys: each entry is the set of field codes unique TOGETHER, which
	 * the backend enforces with a partial unique index.
	 *
	 * Optional because an entity with no natural key is the common case, and a
	 * definition saved before they existed carries none.
	 */
	readonly uniqueKeys?: ReadonlyArray<readonly string[]>;
	/** Os campos que NOMEIAM a linha, na ordem (1–3); ausente = o nome automático (`composed-name.ts`). */
	readonly labelFields?: readonly string[];
	/** As regras do registro (`expr` SQL-like, a frase e o campo que ela nomeia), como o servidor manda. */
	readonly invariants?: readonly { expr: string; message?: string; field?: string }[];
	/** The limits the server enforces on this table; absent or empty when it has none. */
	readonly exclusiveWindows?: readonly unknown[];
	readonly capacities?: readonly unknown[];
	readonly balances?: readonly unknown[];
	readonly quotas?: readonly unknown[];
	/**
	 * Quem pode — o papel mínimo por verbo e o escopo de registros. `scope: "own"`
	 * = cada membro vê e muda só as linhas de que é dono (`owners`: campos
	 * `ref_user` e/ou `created_by`); admin e dono veem tudo. Ausente = todos.
	 */
	readonly access?: {
		readonly read?: "member" | "admin" | "owner";
		readonly write?: "member" | "admin" | "owner";
		readonly scope?: "all" | "own";
		readonly owners?: readonly string[];
	};
	readonly fields: readonly FieldDefinition[];
}

/** Limit and computed-total events exist only on a table that has a limit or a computed total. */
export function raisesEvent(
	entity: Pick<EntityDefinition, "exclusiveWindows" | "capacities" | "balances" | "quotas"> & {
		readonly fields: ReadonlyArray<{ readonly config?: Record<string, unknown> }>;
	},
	event: string,
): boolean {
	if (event.startsWith("limit."))
		return [entity.exclusiveWindows, entity.capacities, entity.balances, entity.quotas].some(
			(limits) => (limits?.length ?? 0) > 0,
		);
	if (event === "derived.crossed") return entity.fields.some((f) => f.config?.derived != null);
	return true;
}

export type DynamicRecord = Record<string, unknown> & { id: string };

/** Live fields, in the order the builder chose. */
export function liveFields(entity: EntityDefinition): FieldDefinition[] {
	return entity.fields.filter((f) => !f.archived).sort((a, b) => a.sortOrder - b.sortOrder);
}

export interface CalendarSpec {
	/** O campo que decide o DIA. `date` ou `datetime`. */
	readonly start: string;
	readonly end?: string;
	/** O que fica escrito na etiqueta. Sem ele, o primeiro campo de texto. */
	readonly title?: string;
	/** Um `select` cujo tom de opção colore a etiqueta. */
	readonly color?: string;
}

/** `calendar` entrou na migração 0072; `component` é a tela desenhada pelo
 *  agente — a fonte SFC mora numa tabela própria e a view só aponta a versão
 *  ativa. O mesmo enum do servidor. */
export type ViewKind = "list" | "form" | "detail" | "calendar" | "component";

export type SortDir = "asc" | "desc";

/** The operators `GET /v3/records/:entity?filter=` accepts. */
export type FilterOp =
	| "eq"
	| "neq"
	| "in"
	| "empty"
	| "filled"
	| "contains"
	| "gt"
	| "gte"
	| "lt"
	| "lte";

/**
 * A field, an operator and a value — the whole grammar of narrowing a list.
 *
 * `value` is `unknown` because `in` takes a list and `empty`/`filled` take
 * nothing; the server validates the pairing and answers 400 `UNKNOWN_FIELD` when
 * the field or the operator does not fit the column.
 */
export interface RecordFilter {
	/** Um código de campo ou uma coluna de sistema (`SystemColumn`). */
	readonly field: SystemColumn | (string & {});
	readonly op: FilterOp;
	readonly value?: unknown;
}

/**
 * What a list is filtered and ordered BY, as the builder saved it.
 *
 * The server has stored and returned both columns since views existed and this
 * type dropped them on the floor, so no screen could obey a saved list.
 *
 * ONE GRAMMAR, NOT TWO. `filterSpec` is exactly what the `filter` query
 * parameter takes, because the server reads both through the same reader
 * (`readFilterSpec`/`listRecordsSchema`): a chip somebody just added and a
 * narrowing a view saved have to mean the same thing, or a saved list and the
 * screen over it drift apart. Reading is deliberately tolerant — the column is
 * jsonb and has accepted anything since migration 0007 — so a spec that is not
 * the grammar reads as "no filter" instead of breaking the screen.
 */
export interface FilterSpec {
	readonly filters?: readonly RecordFilter[];
}

export interface OrderSpec {
	/** A field code, or one of `created_at` / `updated_at` / `id`. */
	readonly sort?: string;
	readonly dir?: SortDir;
	/**
	 * O campo que quebra a lista em grupos — um cabeçalho por valor, com cor e contagem.
	 *
	 * Fica no `orderSpec` e não num campo novo da view porque agrupar É ordenar: as
	 * linhas de um grupo só aparecem juntas se o servidor as ordenar por este campo, e
	 * quem ordena é ele (a lista nunca ordena 25 de 4.000). Guardar as duas decisões em
	 * lugares diferentes seria guardar metade de uma decisão em cada.
	 *
	 * O servidor não mudou para isto: `updateViewSchema` aceita `orderSpec` livre e a
	 * coluna é jsonb desde a 0007.
	 */
	readonly groupBy?: string;
}

/** A column and a direction, as the person clicking a header means it. */
export interface SortSpec {
	readonly field: string;
	readonly dir: SortDir;
}

/** The order a view saved, when it saved a usable one. */
export function sortFromOrderSpec(spec: OrderSpec | undefined): SortSpec | null {
	if (!spec?.sort) return null;
	// `desc` is the fallback because it is the server's own default for a list,
	// so a spec that names a column and no direction orders the same here and
	// there.
	return { field: spec.sort, dir: spec.dir === "asc" ? "asc" : "desc" };
}

/**
 * O agrupamento que a view guardou, quando guardou um usável.
 *
 * Irmão do `sortFromOrderSpec` e igualmente TOLERANTE: a coluna é jsonb e aceita
 * qualquer coisa desde a 0007, então um spec que não é a gramática lê como "sem
 * agrupamento" em vez de quebrar a tela. Quem recebe o código ainda tem de conferir que
 * ele é um campo vivo da entidade — uma view salva antes de o campo ser arquivado
 * continua pedindo por ele.
 */
export function groupFromOrderSpec(spec: OrderSpec | undefined): string | null {
	const code = spec?.groupBy;
	return typeof code === "string" && code.trim() ? code : null;
}

/** The filters a view saved, dropping anything that is not the grammar. */
export function filtersFromSpec(spec: FilterSpec | undefined): readonly RecordFilter[] {
	const raw = spec?.filters;
	if (!Array.isArray(raw)) return [];
	return raw.filter(
		(f): f is RecordFilter =>
			typeof f === "object" &&
			f !== null &&
			typeof f.field === "string" &&
			typeof f.op === "string",
	);
}

/**
 * Como o formulário publicado se apresenta, e o que a resposta vira.
 *
 * Espelha `publicConfigSchema` do servidor campo a campo — inclusive
 * `logoFileId`, que faltava aqui: um campo que o tipo não declara é um campo que
 * o TypeScript apaga em silêncio no caminho até o corpo do publish.
 *
 * O publish SUBSTITUI este objeto inteiro (`write-view.ts`), nunca o mescla, de
 * modo que quem publica manda o objeto completo ou apaga o que não mandou.
 */
export interface PublicViewConfig {
	readonly theme?: "dark" | "light";
	readonly title?: string;
	readonly intro?: string;
	readonly submitLabel?: string;
	readonly accentColor?: string;
	readonly logoFileId?: string;
	readonly after?: { kind: "message"; text: string } | { kind: "redirect"; url: string };
	readonly contactMapping?: { email?: string; phone?: string; name?: string };
}

export interface ViewDefinition {
	readonly id: string;
	readonly code: string;
	readonly name: string;
	/**
	 * Esta pessoa pode ALTERAR esta entidade — a decisão do servidor, desenhada.
	 *
	 * Opcional porque a lista é lida por rotas que não a calculam (o blueprint, o
	 * construtor), e ausente quer dizer "não perguntei", nunca "não pode": quem
	 * desenha um botão a partir dela trata a ausência como o estado de hoje —
	 * aberto — e é o SERVIDOR que recusa, com a frase que nomeia o motivo.
	 */
	readonly canWrite?: boolean;
	readonly description: string | null;
	readonly entityCode: string;
	readonly kind: ViewKind;
	readonly isDefault: boolean;
	readonly recordContext?: boolean;
	readonly fieldCodes: readonly string[];
	readonly calendar?: CalendarSpec;
	readonly isPublic: boolean;
	readonly publicSlug: string | null;
	/** Endereços que o formulário já teve (sai do ar ou troca): respostas antigas trazem um deles em `bx_source`. */
	readonly pastSlugs?: readonly string[];
	readonly publicConfig: PublicViewConfig;
	/** Only a `list` view carries these; the server sends `{}` when unset. */
	readonly filterSpec?: FilterSpec;
	readonly orderSpec?: OrderSpec;
	/**
	 * Só uma view de kind `component` aponta para cá: a versão ATIVA da fonte
	 * SFC. `null` (ou ausente, nas views antigas) = nada publicado ainda —
	 * rollback é reapontar, nunca apagar.
	 */
	readonly componentVersion?: number | null;
}

export interface RelationDefinition {
	/** The child's column that holds the parent's id, as the back declares it. */
	readonly fieldCode: string;
	readonly inverse?: boolean;
	readonly source?: "relation" | "field";
	readonly id: string;
	readonly code: string;
	readonly label: string;
	readonly kind: "many_to_one" | "one_to_many" | "many_to_many";
	readonly fromEntityCode: string;
	readonly toEntityCode: string;
	readonly required: boolean;
	/** Posição da aba do filho no pai, declarada no blueprint; ausente = depois das numeradas. */
	readonly tabOrder?: number;
	/** `false` = o filho não vira aba no pai. */
	readonly showInParent?: false;
}

/**
 * Os filhos que o pai mostra (aba no detalhe, sub-formulário no cadastro), na
 * ordem que o blueprint declarou: `tabOrder` primeiro, as sem número depois na
 * ordem do servidor; `showInParent: false` fica de fora.
 */
export function childrenInParent(
	relations: readonly RelationDefinition[],
	entityCode: string,
): RelationDefinition[] {
	return relations
		.filter(
			(r) =>
				r.kind === "one_to_many" && r.fromEntityCode === entityCode && r.showInParent !== false,
		)
		.sort(
			(a, b) => (a.tabOrder ?? Number.MAX_SAFE_INTEGER) - (b.tabOrder ?? Number.MAX_SAFE_INTEGER),
		);
}
